import React, { ChangeEvent, ClipboardEvent, MouseEvent, useState } from "react";

import { ErrorIcon } from "@/components/icons/ErrorIcon";
import { InputError } from "@/components/ui-kit/InputError/InputError";
import { cn } from "@/utils/general";

import styles from "./InputText.module.scss";

/**
 * Props for the InputText component.
 */
export interface InputTextProps {
  autoComplete?: string;
  className?: string;
  disabled?: boolean;
  id: string;
  errors?: string;
  /**
   * Optional formatter applied to the input value on each change (e.g. kit ID masking).
   * Mutates the event value before calling `onChange` so Formik handlers stay compatible.
   */
  formatOnType?: (value: string) => string;
  icon?: React.ReactNode;
  maxLength?: number;
  minLength?: number;
  name: string | React.ReactNode;
  onBlur?: () => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onPaste?: (e: ClipboardEvent<HTMLInputElement>) => void;
  onMouseDown?: (e: MouseEvent<HTMLInputElement>) => void;
  noBottomSpacing?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  showOptional?: boolean;
  showRequired?: boolean;
  touched?: boolean;
  type?: string;
  visualName?: string | React.ReactNode;
  hideLabel?: boolean;
  value?: string;
  widths?: string;
  inputClassName?: string;
  errorClassName?: string;
}

/**
 * InputText component provides a text input field with a floating label,
 * error handling, and various styling options. Responsive widths are expected
 * to be applied manually via the 'className' prop by the consumer.
 */
export const InputText: React.FC<InputTextProps> = (props) => {
  const {
    visualName,
    hideLabel = false,
    name,
    type,
    id,
    value,
    autoComplete,
    onBlur,
    formatOnType,
    onChange,
    onPaste,
    showRequired,
    showOptional,
    errors,
    touched,
    readOnly,
    disabled,
    placeholder,
    maxLength,
    minLength,
    className,
    icon,
    inputClassName,
    errorClassName,
    noBottomSpacing,
  } = props;

  const [mouseClicked, setMouseClicked] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (formatOnType) {
      e.target.value = formatOnType(e.target.value);
    }
    onChange?.(e);
  };

  const inputLabelClasses = cn(styles.inputLabel, {
    [styles.required]: showRequired,
    [styles.focused]: mouseClicked || placeholder !== undefined,
    [styles.hasValue]: !!value,
  });

  const inputClasses = cn(
    styles.input,
    {
      [styles.mouse]: mouseClicked,
      [styles.withIcon]: !!icon,
    },
    inputClassName
  );

  const labelInputBoxClasses = cn(
    styles.labelInputBox,
    "flex",
    "flex-wrap",
    className,
    noBottomSpacing && styles.noBottomSpacing,
    touched && errors ? styles.error : undefined,
    readOnly ? styles.readonly : undefined,
    disabled ? styles.disabled : undefined
  );

  return (
    <div className={labelInputBoxClasses}>
      {hideLabel ? null : (
        <label htmlFor={id} className={inputLabelClasses}>
          <span className={styles.visualName}>
            {visualName ? visualName : name}
            {showRequired && <span className={styles.requiredAsterisk}>*</span>}
          </span>

          {showOptional && <span className={styles.optional}>(Optional)</span>}
        </label>
      )}

      <div className={styles.inputContainer}>
        <input
          name={id}
          id={id}
          readOnly={!!readOnly}
          disabled={!!disabled}
          type={type}
          value={value}
          onPaste={onPaste}
          autoComplete={autoComplete}
          onChange={handleChange}
          onFocus={() => setMouseClicked(true)}
          onMouseDown={() => setMouseClicked(true)}
          onBlur={() => {
            setMouseClicked(false);
            onBlur?.();
          }}
          className={inputClasses}
          data-testid="textinput"
          placeholder={placeholder ? placeholder : ""}
          maxLength={maxLength ? maxLength : undefined}
          minLength={minLength ? minLength : undefined}
          aria-labelledby={id}
        />

        {touched && errors ? (
          <div className={styles.iconContainer}>
            <ErrorIcon />
          </div>
        ) : (
          <>{icon && <div className={styles.iconContainer}>{icon}</div>}</>
        )}
      </div>

      {touched && errors && <InputError message={errors} className={errorClassName} />}
    </div>
  );
};
